/**
 * RateLimitsTab — US-19-21 I admin read-only display of all rate limit configs.
 * Source: GET /api/admin/rate-limits Worker endpoint.
 */

import React, { useState, useEffect } from 'react';
import { Shield, RefreshCw, Activity, Users, Globe, Network, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

interface RateLimitsData {
    per_user: { concurrent_max: number; rpm_max: number; tpm_max: number; rpd_max: number; min_charge_credits: number; file_size_max_mb: number; description: string };
    global_vertex: { tpm_max: number; rpm_max: number; rpd_max: number; tpm_current: number; rpm_current: number; rpd_current: number; tpm_pct: string; rpm_pct: string; rpd_pct: string; description: string; source: string };
    supply_cap: { promotion_max_per_month: number; promotion_used_this_month: number; promotion_pct: string; month_bucket: string; promotion_credits_per_grant: number; description: string };
    signup_protection: { otp_per_phone: string; cf_waf_jobs: string; cf_waf_signup: string; cf_waf_otp: string; description: string };
    observability: { log_fields: string[]; recover_stale_jobs_threshold_min: number; heartbeat_threshold_sec: number };
    last_updated: string;
}

const RateLimitsTab: React.FC = () => {
    const { session } = useAuth();
    const [data, setData] = useState<RateLimitsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        setLoading(true); setError(null);
        try {
            const res = await fetch(`${WORKERS_URL}/api/admin/rate-limits`, {
                headers: { Authorization: `Bearer ${session?.access_token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setData(await res.json());
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    if (loading && !data) return <div className="p-6 text-slate-500">Đang tải...</div>;
    if (error) return <div className="p-6 text-red-600">Lỗi: {error}</div>;
    if (!data) return null;

    const pctColor = (pct: string) => {
        const p = parseFloat(pct);
        if (p > 80) return 'text-red-600 bg-red-50';
        if (p > 50) return 'text-amber-600 bg-amber-50';
        return 'text-emerald-600 bg-emerald-50';
    };

    return (
        <div className="p-6 space-y-6 overflow-y-auto h-full">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-indigo-600" />
                    <h2 className="text-lg font-bold text-slate-800">Rate Limits & Anti-abuse</h2>
                </div>
                <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>

            {/* Per-user caps */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-blue-600" />
                    <h3 className="font-bold text-slate-700">Per-user caps (fairness + UX)</h3>
                </div>
                <p className="text-xs text-slate-500 mb-3">{data.per_user.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <Metric label="Concurrent jobs/user" val={data.per_user.concurrent_max} />
                    <Metric label="RPM/user" val={data.per_user.rpm_max} suffix="/phút" />
                    <Metric label="TPM/user" val={data.per_user.tpm_max} suffix="tokens/phút" formatter={n => (n / 1_000_000).toFixed(1) + 'M'} />
                    <Metric label="RPD/user" val={data.per_user.rpd_max} suffix="/ngày" />
                    <Metric label="Min charge" val={data.per_user.min_charge_credits} suffix="credits/call" />
                    <Metric label="File size max" val={data.per_user.file_size_max_mb} suffix="MB" />
                </div>
            </section>

            {/* Global Vertex caps */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-purple-600" />
                    <h3 className="font-bold text-slate-700">Global Vertex AI hard caps</h3>
                </div>
                <p className="text-xs text-slate-500 mb-3">{data.global_vertex.description}</p>
                <p className="text-[11px] text-slate-400 mb-3">Source: {data.global_vertex.source}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <UsageBar label="TPM" current={data.global_vertex.tpm_current} max={data.global_vertex.tpm_max} pct={data.global_vertex.tpm_pct} format={n => (n / 1_000_000).toFixed(2) + 'M'} colorClass={pctColor(data.global_vertex.tpm_pct)} />
                    <UsageBar label="RPM" current={data.global_vertex.rpm_current} max={data.global_vertex.rpm_max} pct={data.global_vertex.rpm_pct} format={n => n.toLocaleString()} colorClass={pctColor(data.global_vertex.rpm_pct)} />
                    <UsageBar label="RPD" current={data.global_vertex.rpd_current} max={data.global_vertex.rpd_max} pct={data.global_vertex.rpd_pct} format={n => n.toLocaleString()} colorClass={pctColor(data.global_vertex.rpd_pct)} />
                </div>
            </section>

            {/* Supply cap */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-4 h-4 text-emerald-600" />
                    <h3 className="font-bold text-slate-700">Supply cap (promotion / month)</h3>
                </div>
                <p className="text-xs text-slate-500 mb-3">{data.supply_cap.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <UsageBar label={`Promotion ${data.supply_cap.month_bucket}`} current={data.supply_cap.promotion_used_this_month} max={data.supply_cap.promotion_max_per_month} pct={data.supply_cap.promotion_pct} format={n => n.toString()} colorClass={pctColor(data.supply_cap.promotion_pct)} />
                    <Metric label="Credits/grant" val={data.supply_cap.promotion_credits_per_grant} formatter={n => (n / 1000) + 'K'} />
                    <Metric label="Auto-reset" val={0} suffix="đầu mỗi tháng" formatter={() => '✓'} />
                </div>
            </section>

            {/* Network protection */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                    <Network className="w-4 h-4 text-amber-600" />
                    <h3 className="font-bold text-slate-700">Network / signup protection</h3>
                </div>
                <p className="text-xs text-slate-500 mb-3">{data.signup_protection.description}</p>
                <div className="space-y-2 text-xs">
                    <Row label="OTP" val={data.signup_protection.otp_per_phone} />
                    <Row label="WAF /api/jobs" val={data.signup_protection.cf_waf_jobs} />
                    <Row label="WAF /signup" val={data.signup_protection.cf_waf_signup} />
                    <Row label="WAF /otp-verify" val={data.signup_protection.cf_waf_otp} />
                </div>
            </section>

            {/* Observability */}
            <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-4 h-4 text-slate-600" />
                    <h3 className="font-bold text-slate-700">Observability</h3>
                </div>
                <div className="space-y-2 text-xs">
                    <Row label="Stale jobs recovery" val={`${data.observability.recover_stale_jobs_threshold_min} phút`} />
                    <Row label="Heartbeat threshold (incognito)" val={`${data.observability.heartbeat_threshold_sec} giây`} />
                    <Row label="Log fields" val={data.observability.log_fields.join(', ')} />
                </div>
            </section>

            <p className="text-[10px] text-slate-400 text-right">Last updated: {new Date(data.last_updated).toLocaleString('vi-VN')}</p>
        </div>
    );
};

function Metric({ label, val, suffix = '', formatter }: { label: string; val: number; suffix?: string; formatter?: (n: number) => string }) {
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{label}</div>
            <div className="text-lg font-bold text-slate-800 mt-1">{formatter ? formatter(val) : val.toLocaleString()}<span className="text-xs font-normal text-slate-500 ml-1">{suffix}</span></div>
        </div>
    );
}

function UsageBar({ label, current, max, pct, format, colorClass }: { label: string; current: number; max: number; pct: string; format: (n: number) => string; colorClass: string }) {
    const p = Math.min(parseFloat(pct), 100);
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{label}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${colorClass}`}>{pct}%</span>
            </div>
            <div className="text-sm font-bold text-slate-800">{format(current)} <span className="text-xs text-slate-400 font-normal">/ {format(max)}</span></div>
            <div className="w-full bg-slate-200 h-1.5 rounded-full mt-2 overflow-hidden">
                <div className={`h-full transition-all ${p > 80 ? 'bg-red-500' : p > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${p}%` }} />
            </div>
        </div>
    );
}

function Row({ label, val }: { label: string; val: string }) {
    return (
        <div className="flex justify-between border-b border-slate-100 py-1.5">
            <span className="font-bold text-slate-600">{label}</span>
            <span className="text-slate-700">{val}</span>
        </div>
    );
}

export default RateLimitsTab;
